import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Popover from 'shared/components/Popover';
import { cn } from 'shared/lib/utils';

export interface FormAutocompleteOption {
  value: string;
  label: string;
}

interface FormAutocompleteProps {
  label: string;
  options: FormAutocompleteOption[];
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
}

/**
 * Searchable single-select input. The visible text is a free-typing filter;
 * the actual form value is one of `options[*].value`. The component is fully
 * controlled — wire it through react-hook-form's `Controller`.
 *
 * Styling matches FormSelect / FormInput (Tailwind + .input-modern). Built
 * on a plain text input + listbox so we don't pull in MUI / react-select for
 * a single widget.
 */
const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
  label,
  options,
  value,
  onChange,
  onBlur,
  error,
  hint,
  required,
  placeholder,
  disabled,
  className,
  id,
  name,
}) => {
  const { t } = useTranslation();
  const inputId =
    id || `field-${label.replace(/\s+/g, '-').toLowerCase()}-autocomplete`;
  const errorMessage = error
    ? (t(`validation.${error}`, { defaultValue: error }) as string)
    : undefined;

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState<string>(selectedOption?.label ?? '');
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Sync the visible query with the selected option whenever the form value
  // changes externally (e.g. when react-hook-form resets the section).
  useEffect(() => {
    setQuery(selectedOption?.label ?? '');
  }, [selectedOption]);

  // Close on click outside.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
        // Restore visible text to the committed value to avoid leaving a
        // stale filter string in the input.
        setQuery(selectedOption?.label ?? '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, selectedOption]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q === selectedOption?.label.toLowerCase()) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, selectedOption]);

  const commit = (opt: FormAutocompleteOption) => {
    onChange(opt.value);
    setQuery(opt.label);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) setIsOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && activeIndex >= 0 && filtered[activeIndex]) {
        e.preventDefault();
        commit(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery(selectedOption?.label ?? '');
    }
  };

  // Scroll the active item into view as the user keyboard-navigates.
  useEffect(() => {
    if (!isOpen || activeIndex < 0 || !listRef.current) return;
    const child = listRef.current.children[activeIndex] as
      | HTMLElement
      | undefined;
    child?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const clear = () => {
    onChange('');
    setQuery('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div
      className="flex flex-col gap-2 w-full"
      ref={containerRef}
      data-error={error ? 'true' : undefined}
    >
      <label
        htmlFor={inputId}
        className="text-base font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-gray-500 ms-1">*</span>}
      </label>
      <div className="relative" ref={fieldRef}>
        <input
          id={inputId}
          ref={inputRef}
          name={name}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${inputId}-listbox`}
          aria-invalid={!!error}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            'input-modern pe-10',
            error && 'has-error',
            disabled && 'opacity-60 cursor-not-allowed',
            className,
          )}
        />
        {value ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={clear}
            disabled={disabled}
            aria-label={t('common.clear', { defaultValue: 'Clear' }) as string}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        ) : (
          <ChevronDown
            size={18}
            className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
        )}

        <Popover
          anchorRef={fieldRef}
          open={isOpen && !disabled}
          panelRef={panelRef}
          className="rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          {filtered.length > 0 ? (
            <ul id={`${inputId}-listbox`} ref={listRef} role="listbox">
              {filtered.map((opt, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = opt.value === value;
                return (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(opt);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={cn(
                      'cursor-pointer px-3 py-2.5 text-base',
                      isActive && 'bg-gray-100',
                      isSelected && 'font-semibold text-gray-900',
                    )}
                  >
                    {opt.label}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-3 py-2.5 text-base text-gray-500">
              {t('common.noResults', { defaultValue: 'No results' })}
            </div>
          )}
        </Popover>
      </div>
      {errorMessage ? (
        <span className="text-xs font-medium text-red-600">{errorMessage}</span>
      ) : hint ? (
        <span className="text-xs text-gray-500">{hint}</span>
      ) : null}
    </div>
  );
};

export default FormAutocomplete;
